import {
  Component,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
  SimpleChanges,
  ViewChild,
  Input,
  ViewEncapsulation,
  EventEmitter,
  Output,
  AfterViewInit,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { InboundOrderDto } from 'src/app/domain/models/dto/inbound-order.dto';
import { BoobleModalService } from 'booble-modal';
import { ActivatedRoute } from '@angular/router';
import { AdminOrderDetailModalComponent } from '../modals/order-details-modal/admin-order-detail-modal.component';
import { OrderReviewModalComponent } from '../modals/order-review-modal/order-review-modal.component';
import { FinancialReconciliationModalComponent } from '../modals/financial-reconciliation-modal/financial-reconciliation-modal.component';
import { QuotationReconciliationModalComponent } from '../modals/quotation-reconciliation-modal/quotation-reconciliation-modal.component';
import { AdminOrderEditModal } from '../modals/order-edit-modal/admin-order-edit-modal.component';
import { TreasuryInfoComponent } from '../modals/treasury-info/treasury-info.component';
import { ExchangeInfoComponent } from '../modals/exchange-info/exchange-info.component';
import { PaymentConfirmComponent } from '../modals/payment-confirm/payment-confirm.component';
import { SettlementInfoComponent } from '../modals/settlement-info/settlement-info.component';
import { ConfirmExteriorPaymentComponent } from '../modals/confirm-exterior-payment/confirm-exterior-payment.component';
import { ResponseTableModel } from 'src/app/domain/models/response-table.model';
import {
  getStatusClass,
  getStatusLabel,
} from 'src/app/shared/utils/client-status.util';
import { ExecutionOrderComponent } from '../modals/execution-order/execution-order.component';
import { MassPaymentConfirmComponent } from '../modals/mass-payment-confirm/mass-payment-confirm.component';
import {
  columnWidthsDefault,
  tabMappingsOutbound,
  tabMappingsInbound,
} from '../utils/tabMapping';
import { SettlementInfoConfirmComponent } from '../modals/settlement-info-confirm/settlement-info-confirm.component';
import { SendContractComponent } from '../modals/send-contract/send-contract.component';

@Component({
  selector: 'app-table-orders-admin',
  templateUrl: './table-orders-admin.component.html',
  styleUrls: ['./table-orders-admin.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableOrdersAdminComponent
  implements OnInit, OnChanges, AfterViewInit
{
  rType: string = '';
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @Input() data: ResponseTableModel<InboundOrderDto[]>;
  @Input() displayedColumns: string[];
  @Input() pageNumber: number = 0;
  @Input() pageSize: number = 10;
  @Input() totalElements: number;
  @Input() currentTab: string = '';

  @Output() pageChange = new EventEmitter<{
    pageNumber: number;
    pageSize: number;
  }>();

  selection = new SelectionModel<any>(true, []);
  public dataSource: MatTableDataSource<InboundOrderDto>;

  constructor(
    private modalService: BoobleModalService,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const rType = params.get('rType');
      if (rType) this.rType = params.get('rType');
    });
    this.initializeDataSource();
  }

  ngAfterViewInit(): void {
    this.initializeTableSort();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data || changes.currentTab) {
      this.initializeDataSource();
      this.initializeTableSort();
    }
  }

  initializeTableSort() {
    if (this.sort) {
      this.sort.active = 'id';
      this.sort.direction = 'desc';
    }

    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'cnpj':
          return item.tipoPessoa === 'PJ'
            ? item.corporateAccount.cnpj
            : item.individualAccount?.cpf;
        case 'companyName':
          return item.tipoPessoa === 'PJ'
            ? item.corporateAccount?.companyName
            : item.individualAccount?.name;
        case 'clientInfo':
          return item.tipoPessoa === 'PJ'
            ? item.corporateAccount?.companyName
            : item.individualAccount?.name;
        case 'id':
          return item.id;
        case 'amountMN':
          return item.quotations[0]?.amountMN || 0;
        default:
          return item[property];
      }
    };

    // Na aba "Ordens cotadas", usar sortData customizado para manter confirmationTreasury=false primeiro
    if (this.currentTab === 'Ordens cotadas') {
      this.dataSource.sortData = (data: InboundOrderDto[], sort: MatSort) => {
        const active = sort.active;
        const direction = sort.direction;

        if (!active || direction === '') {
          return data.sort((a, b) => {
            const aConfirmed = (a as any).confirmationTreasury ? 1 : 0;
            const bConfirmed = (b as any).confirmationTreasury ? 1 : 0;
            return aConfirmed - bConfirmed;
          });
        }

        return data.sort((a, b) => {
          const aConfirmed = (a as any).confirmationTreasury ? 1 : 0;
          const bConfirmed = (b as any).confirmationTreasury ? 1 : 0;

          if (aConfirmed !== bConfirmed) {
            return aConfirmed - bConfirmed;
          }

          const valueA = this.dataSource.sortingDataAccessor(a, active);
          const valueB = this.dataSource.sortingDataAccessor(b, active);

          let comparatorResult = 0;
          if (valueA != null && valueB != null) {
            if (valueA > valueB) {
              comparatorResult = 1;
            } else if (valueA < valueB) {
              comparatorResult = -1;
            }
          } else if (valueA != null) {
            comparatorResult = 1;
          } else if (valueB != null) {
            comparatorResult = -1;
          }

          return comparatorResult * (direction === 'asc' ? 1 : -1);
        });
      };
    }

    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  private initializeDataSource() {
    const content = this.data?.content || [];
    this.dataSource = new MatTableDataSource(content);
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      this.paginator.length = this.totalElements;
    }
  }

  chanePageEvent(event: { pageIndex: number; pageSize: number }) {
    this.pageNumber = event.pageIndex;
    this.pageSize = event.pageSize;

    sessionStorage.setItem('orders-admin-page-size', this.pageSize.toString());
    sessionStorage.setItem(
      'orders-admin-page-number',
      this.pageNumber.toString(),
    );

    this.pageChange.emit({
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
    });
  }

  isAllSelected() {
    return this.selection.selected?.length === this.dataSource.data.length;
  }

  toggleAll() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
  }

  getSelectedRows(): InboundOrderDto[] {
    return this.selection.selected;
  }

  goEditOrder(order: InboundOrderDto) {
    this.modalService.present(AdminOrderEditModal, order);
  }

  openMassPaymentConfirm() {
    const selectedOrders = this.selection.selected;
    this.modalService.present(MassPaymentConfirmComponent, selectedOrders);
  }

  goOrderDetail(orderId: number) {
    this.modalService.present(AdminOrderDetailModalComponent, orderId);
  }

  conciliate(order: InboundOrderDto) {
    this.modalService.present(QuotationReconciliationModalComponent, order);
  }

  openOrderReviewModal(order: InboundOrderDto) {
    this.modalService.present(OrderReviewModalComponent, order);
  }

  openTreasuryInfoModal(order: InboundOrderDto) {
    this.modalService.present(TreasuryInfoComponent, order);
  }

  openConfirmPaymentExteriorModal(order: InboundOrderDto) {
    this.modalService.present(ConfirmExteriorPaymentComponent, order);
  }

  openExchangeInfoModal(order: InboundOrderDto) {
    this.modalService.present(ExchangeInfoComponent, order);
  }

  openExecutionOrderModal(order: InboundOrderDto) {
    this.modalService.present(ExecutionOrderComponent, order);
  }

  openSettlementInfoModal(order: InboundOrderDto) {
    this.modalService.present(SettlementInfoComponent, order);
  }

  openSettlementInfoConfirmModal(order: InboundOrderDto) {
    this.modalService.present(SettlementInfoConfirmComponent, order);
  }

   openSendContractModal(order: InboundOrderDto) {
    this.modalService.present(SendContractComponent, order);
  }
    
  openPaymentConfirmModal(order: InboundOrderDto) {
    this.modalService.present(PaymentConfirmComponent, order);
  }

  openFinancialReconciliationModal(order: InboundOrderDto) {
    this.modalService.present(FinancialReconciliationModalComponent, order);
  }

  getStatusClass = getStatusClass;
  getStatusLabel = getStatusLabel;

  getCommercialRate(row: InboundOrderDto): string | null {
    const quotation = row?.quotations?.[0] as any;
    if (!quotation?.rate || quotation?.spread == null) {
      return null;
    }

    const rate = quotation.rate;
    const spread = quotation.spread;
    let commercial: number;

    if (row.remmitanceType === 'OUTBOUND') {
      commercial = Number((rate + rate * spread).toPrecision(5));
    } else if (row.remmitanceType === 'INBOUND') {
      commercial = Number((rate - rate * spread).toPrecision(5));
    } else {
      return null;
    }

    return commercial.toString().replace('.', ',');
  }

  getColumnWidth(column: string): string {
    const tabMapping =
      this.rType === '1'
        ? tabMappingsOutbound[
            this.currentTab as keyof typeof tabMappingsOutbound
          ]
        : tabMappingsInbound[
            this.currentTab as keyof typeof tabMappingsInbound
          ];

    const widths = tabMapping?.columnWidths || {};
    return widths[column] || columnWidthsDefault[column] || 'auto';
  }
}
